// src/pages/KganyaScents.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Grid3X3, List, SlidersHorizontal, ChevronDown, Search, X } from 'lucide-react';

import { useProducts } from '../features/october/hooks/useProducts';
import { useProductFilters } from '../features/october/hooks/useProductFilters';
import ProductGrid from '../features/october/components/ProductGrid';
import ProductFilters from '../features/october/components/ProductFilters';
import FloatingCartButton from '../features/october/components/FloatingCartButton';


const KganyaScents = () => {
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  
  const { products, loading, error } = useProducts();
  const { filters, updateFilter, resetFilters, hasActiveFilters } =
    useProductFilters();

  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );
      const isSmallScreen = window.innerWidth < 768;
      setIsMobile(isMobileDevice || isSmallScreen);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const filteredProducts = useMemo(() => {
    let filtered = products;
    
    if (searchQuery.trim()) {
      filtered = filtered.filter(
        (product) =>
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.type?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    if (filters.category && filters.category !== 'all') {
      filtered = filtered.filter((product) => product.category === filters.category);
    }
    
    if (filters.gender && filters.gender !== 'all') {
      filtered = filtered.filter(
        (product) =>
          product.gender === filters.gender || product.category === filters.gender
      );
    }

    if (filters.type && filters.type !== 'all') {
      filtered = filtered.filter((product) => product.type === filters.type);
    }

    if (filters.intensity && filters.intensity !== 'all') {
      filtered = filtered.filter((product) => product.intensity === filters.intensity);
    }
    
    if (filters.inStock) {
      filtered = filtered.filter((product) => product.inStock);
    }
    
    if (filters.featured) {
      filtered = filtered.filter((product) => product.featured);
    }

    if (filters.priceRange) {
      filtered = filtered.filter((product) => {
        const price = product.priceValue || 0;
        return price >= filters.priceRange[0] && price <= filters.priceRange[1];
      });
    }

    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'price':
          return (a.priceValue || 0) - (b.priceValue || 0);
        case 'price-desc':
          return (b.priceValue || 0) - (a.priceValue || 0);
        case 'rating':
          return (b.rating || 0) - (a.rating || 0);
        case 'newest':
        default:
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      }
    });
  }, [products, searchQuery, filters, sortBy]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-black text-brand-light">
        <div className="text-center px-6 pt-20 space-y-4">
          <h2 className="text-3xl font-heading text-brand-gold">Something went wrong</h2>
          <p className="text-brand-light/70 font-body">{error}</p>
        </div>
      </div>
    );
  }

  const activeFilterCount = Object.values(filters).filter(v => v && v !== 'all').length;

  return (
    <div className="bg-brand-black min-h-screen text-brand-light">
      <main>

        {/* ── PAGE BANNER ─────────────────────────────────────────── */}
        <section className="relative pt-28 pb-14 px-5 sm:px-8 lg:px-12 overflow-hidden border-b border-white/5">
          {/* subtle radial glow */}
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_60%_55%_at_50%_0%,rgba(230,0,0,0.08),transparent)]" />
          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6"
            >
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-brand-gold/60 mb-3">
                  Pontso Yarona Group
                </p>
                <h1 className="text-5xl sm:text-6xl font-heading text-brand-light leading-tight">
                  Kganya <span className="text-brand-gold">Scents</span>
                </h1>
                <p className="mt-3 text-brand-light/45 font-body text-sm max-w-sm">
                  Handcrafted fragrances, candles, and body rituals rooted in South African botanicals.
                </p>
              </div>

              {/* stat pills */}
              <div className="flex gap-3 flex-wrap">
                {[
                  { label: 'Products', value: loading ? '—' : products.length },
                  { label: 'All natural', value: '100%' },
                  { label: 'Made in SA', value: '✦' },
                ].map((s) => (
                  <div key={s.label} className="px-5 py-3 rounded-full border border-white/6 bg-white/2 text-center min-w-[80px]">
                    <p className="text-base font-heading text-brand-gold leading-none">{s.value}</p>
                    <p className="text-[10px] uppercase tracking-wider text-brand-light/30 mt-1">{s.label}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* ── TOOLBAR ─────────────────────────────────────────────── */}
        <div className="sticky top-[70px] z-20 border-b border-white/5 bg-brand-black/90 backdrop-blur-lg">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-3 flex flex-wrap items-center gap-3">

            {/* Search */}
            <div className="relative flex-1 min-w-[160px] max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-light/30 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search scents…"
                className="w-full pl-9 pr-8 py-2.5 rounded-full bg-white/4 border border-white/8 focus:border-brand-gold/30 text-sm text-brand-light placeholder:text-brand-light/25 outline-none transition-colors"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-light/30 hover:text-brand-light transition-colors">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Sort */}
            <div className="relative">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="appearance-none rounded-full bg-white/4 border border-white/8 pl-4 pr-9 py-2.5 text-sm font-body text-brand-light focus:outline-none focus:border-brand-gold/30 transition-colors"
              >
                <option value="newest" className="bg-brand-black">Newest first</option>
                <option value="name"   className="bg-brand-black">Name A–Z</option>
                <option value="price"  className="bg-brand-black">Price: low–high</option>
                <option value="price-desc" className="bg-brand-black">Price: high–low</option>
                <option value="rating" className="bg-brand-black">Top rated</option>
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-light/30" />
            </div>

            {/* View toggle */}
            <div className="flex rounded-full border border-white/8 bg-white/4 overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-3.5 py-2.5 transition-colors ${viewMode === 'grid' ? 'bg-brand-red text-white' : 'text-brand-light/40 hover:text-brand-light'}`}
              >
                <Grid3X3 size={16} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3.5 py-2.5 transition-colors ${viewMode === 'list' ? 'bg-brand-red text-white' : 'text-brand-light/40 hover:text-brand-light'}`}
              >
                <List size={16} />
              </button>
            </div>

            {/* Filter toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-body transition-all ${
                showFilters
                  ? 'border-brand-gold bg-brand-gold/10 text-brand-gold'
                  : 'border-white/8 bg-white/4 text-brand-light/60 hover:text-brand-light hover:border-white/20'
              }`}
            >
              <SlidersHorizontal size={15} />
              Filters
              {activeFilterCount > 0 && (
                <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-brand-red text-[10px] font-medium text-white">
                  {activeFilterCount}
                </span>
              )}
            </button>

            {/* Product count */}
            <p className="ml-auto text-xs text-brand-light/30 font-body hidden sm:block">
              {loading ? 'Loading…' : `${filteredProducts.length} result${filteredProducts.length !== 1 ? 's' : ''}`}
            </p>
          </div>
        </div>

        {/* ── COLLECTION ──────────────────────────────────────────── */}
        <section id="collection" className="px-5 sm:px-8 lg:px-12 py-10">
          <div className="max-w-7xl mx-auto">

            {/* Expandable filters */}
            <AnimatePresence>
              {showFilters && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
                  className="overflow-hidden mb-8"
                >
                  <div className="rounded-2xl border border-white/6 bg-white/2 p-6">
                    <ProductFilters
                      products={products}
                      filters={filters}
                      updateFilter={updateFilter}
                      searchQuery={searchQuery}
                      onSearchChange={setSearchQuery}
                    />
                    {hasActiveFilters && (
                      <div className="mt-4 pt-4 border-t border-white/6 text-right">
                        <button
                          onClick={resetFilters}
                          className="text-xs text-brand-light/40 hover:text-brand-gold transition-colors"
                        >
                          Clear all filters
                        </button>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Grid */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <ProductGrid
                products={filteredProducts}
                loading={loading}
                error={error}
                viewMode={viewMode}
                emptyMessage="No scents matched your refinement."
                emptyDescription="Adjust your filters or explore all products to discover the full Kganya spectrum."
              />
            </motion.div>

          </div>
        </section>
      </main>

      {/* Floating cart */}
      <motion.div
        className={`fixed z-30 ${isMobile ? 'bottom-6 right-6' : 'bottom-8 right-8'}`}
        initial={{ opacity: 0, scale: 0, rotate: -180 }}
        animate={{ opacity: 1, scale: 1, rotate: 0 }}
        transition={{ delay: 1.2, type: 'spring', stiffness: 200, damping: 16 }}
      >
        <FloatingCartButton />
      </motion.div>

      {/* Loading overlay */}
      <AnimatePresence>
        {loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 flex items-center justify-center bg-brand-black/90 backdrop-blur-md"
          >
            <div className="text-center">
              <motion.div
                className="mx-auto mb-6 h-16 w-16 rounded-full border-4 border-brand-gold border-t-brand-red"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              />
              <p className="text-sm font-body text-brand-light/60">
                Preparing your sensory journey…
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default KganyaScents;


